import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { mountPdfPages } from "../lib/pdfPages";
import { Button } from "./Button";
import { Spinner } from "./Spinner";

interface DocumentViewerProps {
  /** The PDF bytes from an authed `apiFetch(..., { responseType: "blob" })`. */
  file: Blob;
  /** Announced as the document's accessible name (and shown in the full-screen bar). */
  title: string;
}

type Status = "loading" | "ready" | "error";

/**
 * Shows a prefilled PDF inline, with a full-screen overlay for reading.
 *
 * pdf.js (`lib/pdfPages.ts`) draws one `<canvas>` per page plus a transparent
 * `TextLayer`, so screen readers can read the form and its text is selectable.
 * Drawing the pages here, rather than in an `<iframe>`, keeps the browser's PDF
 * toolbar and thumbnail sidebar out. The enrollment e-sign ceremony and the
 * member CMS-10882 signing view use it.
 *
 * Full screen is a fixed-position overlay, not the native Fullscreen API:
 * `element.requestFullscreen()` is a no-op on non-`<video>` elements in iPhone
 * Safari, so it would silently do nothing for many patients.
 *
 * `file` is the PDF `Blob` itself. pdf.js gets the bytes via
 * `getDocument({ data })`, so the viewer mints no `blob:` object URL and
 * parents own no URL lifecycle.
 *
 * Inline, the viewer is a static, clipped preview of page 1 under a full-size
 * button, so a click, tap or Enter anywhere on it opens full screen. The
 * button's hint shows on hover or focus, and always on `hover: none` devices.
 * Reading, scrolling and text selection happen in full screen.
 *
 * The document is parsed once per `file`, never on the full-screen toggle. The
 * pages container stays mounted across the toggle: only wrapper classNames
 * change, and the chrome (top bar, overlay button) renders conditionally with
 * `&&` so the container keeps a stable position among its siblings. The width
 * change on toggle re-renders the pages through the same ResizeObserver path
 * as a window resize.
 */
export function DocumentViewer({ file, title }: DocumentViewerProps): ReactNode {
  const [fullscreen, setFullscreen] = useState(false);
  // Keyed by `file` so a different blob reads as "loading" without an
  // effect-time setState; `mountPdfPages` reports back for the file it was
  // mounted with.
  const [loaded, setLoaded] = useState<{ file: Blob; status: Exclude<Status, "loading"> } | null>(
    null,
  );
  const status: Status = loaded?.file === file ? loaded.status : "loading";
  const pagesRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    const host = pagesRef.current;
    if (!host) return;
    return mountPdfPages(host, file, {
      onReady: () => setLoaded({ file, status: "ready" }),
      onError: () => setLoaded({ file, status: "error" }),
    });
  }, [file]);

  // While full screen: lock body scroll and close on Escape. Both are gated on
  // `fullscreen` (this component, unlike a modal, is always mounted) and clean
  // up on collapse and on unmount.
  useEffect(() => {
    if (!fullscreen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [fullscreen]);

  return (
    <div
      role={fullscreen ? "dialog" : undefined}
      aria-modal={fullscreen ? true : undefined}
      aria-labelledby={fullscreen ? titleId : undefined}
      className={
        fullscreen
          ? "fixed inset-0 z-50 flex flex-col bg-slate-900/80"
          : "group relative flex flex-col"
      }
    >
      {fullscreen && (
        <div className="flex items-center justify-between gap-3 bg-white px-4 py-3 shadow-sm">
          <span id={titleId} className="truncate text-sm font-medium text-slate-700">
            {title}
          </span>
          <Button variant="secondary" size="sm" onClick={() => setFullscreen(false)}>
            Exit full screen
          </Button>
        </div>
      )}

      <div
        className={
          fullscreen
            ? "relative min-h-0 flex-1 overflow-y-auto bg-slate-900 [scrollbar-gutter:stable]"
            : "relative h-[60vh] w-full overflow-hidden rounded-md border border-slate-300 bg-white"
        }
      >
        <div
          ref={pagesRef}
          role="document"
          aria-label={title}
          className={
            fullscreen
              ? "mx-auto flex w-full max-w-4xl flex-col gap-3 py-3"
              : "flex w-full flex-col gap-3"
          }
        />
        {status === "loading" && (
          <p className="text-note text-slate-blue absolute inset-0 flex items-center justify-center gap-3 bg-white">
            <Spinner /> Preparing your form…
          </p>
        )}
        {status === "error" && (
          <p
            role="alert"
            className="text-note text-danger absolute inset-0 flex items-center justify-center bg-white"
          >
            We couldn&apos;t display the form.
          </p>
        )}
      </div>

      {!fullscreen && status === "ready" && (
        <button
          type="button"
          aria-label="View the form full screen"
          onClick={() => setFullscreen(true)}
          className="bg-midnight/30 absolute inset-0 flex cursor-pointer items-center justify-center rounded-md opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
        >
          <span className="text-slate-blue text-note rounded-full bg-white/85 px-3.5 py-2">
            <span className="[@media(hover:none)]:hidden">Click for fullscreen</span>
            <span className="hidden [@media(hover:none)]:inline">Tap for fullscreen</span>
          </span>
        </button>
      )}
    </div>
  );
}
